import { useRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import './ui.css';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'icon';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  block?: boolean;
  large?: boolean;
}

export function Button({
  variant = 'primary',
  block = false,
  large = false,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    `btn--${variant}`,
    block ? 'btn--block' : '',
    large ? 'btn--large' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <button type={type} className={classes} {...rest} />;
}

interface CardProps {
  children: ReactNode;
  padded?: boolean;
  section?: boolean;
  className?: string;
}

/**
 * Children are rendered directly, with no inner wrapper.
 *
 * An inner element would swallow any layout the caller sets through
 * `className`: a `display: grid` meant for the tiles would land on the card and
 * find a single child. Cards that carry a decorative `Ornament` raise their own
 * content with `z-index: 1` instead — see design/direction.md.
 */
export function Card({ children, padded = true, section = false, className }: CardProps) {
  const classes = [
    'card',
    padded ? 'card--padded' : '',
    section ? 'card--section' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <div className={classes}>{children}</div>;
}

interface SegmentedProps<T extends string | number> {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  format?: (value: T) => string;
  label: string;
}

/**
 * A one-of-several choice, exposed as a radio group.
 *
 * These were toggle buttons with `aria-pressed`, which describes several
 * independent switches that happen to sit together — a screen reader announced
 * "4, pressed" with no hint that picking it released the others. A radio group
 * says "2 of 3" and supports the arrow keys people expect.
 */
export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  format,
  label,
}: SegmentedProps<T>) {
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (index: number, step: number) => {
    const next = (index + step + options.length) % options.length;
    onChange(options[next]);
    // In a radio group the arrow keys move focus *with* the selection; without
    // this, focus stays parked on a tabIndex="-1" button and the roving
    // tabindex is a claim the keyboard cannot cash.
    buttonsRef.current[next]?.focus();
  };

  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((option, index) => {
        const selected = option === value;
        return (
          <button
            key={String(option)}
            ref={(node) => {
              buttonsRef.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            // Roving tabindex: the group is one tab stop, arrows move within it.
            tabIndex={selected ? 0 : -1}
            className="segmented__option"
            onClick={() => onChange(option)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                move(index, 1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                move(index, -1);
              }
            }}
          >
            {format ? format(option) : String(option)}
          </button>
        );
      })}
    </div>
  );
}

interface SectionTitleProps {
  children: ReactNode;
  id?: string;
}

export function SectionTitle({ children, id }: SectionTitleProps) {
  return (
    <h2 className="section-title" id={id}>
      {children}
    </h2>
  );
}
